import type { ReactNode } from 'react';
import { Box, Button, Stack, Typography } from '../primitives';
import * as Icons from '../icons';
import { iconSize } from '@munifocus/tokens';

export interface DetailPageHeaderProps {
  /** Already-localized label for the back control, e.g. "Back". */
  backLabel: string;
  onBack: () => void;
  /** The record's name — what this page is *about*. */
  title: string;
  /** A status chip or similar, shown beside the title. */
  badge?: ReactNode;
  /** Second line, for the parent record or a qualifier. */
  subtitle?: string;
  /** Page-level actions, right-aligned. Usually one Edit or Save/Cancel pair. */
  actions?: ReactNode;
}

/**
 * The header every detail and edit page shares: back, title, actions, one line.
 *
 * **Why this is a component and not six copies of a Stack.** The pages that grew
 * out of the contract and appointment dialogs each stacked a lone Back button
 * above the record's name, which reads as a floating control with nothing to
 * anchor it — the eye has to travel down to find out what it goes back *from*.
 * Putting the name on the same line makes the row say "this record, and the way
 * out of it", and one component is what keeps the admin and Government portals
 * from drifting apart on it.
 *
 * Actions sit at the far end rather than beside the title: they act on the whole
 * record, and grouping them with its name implies they act on the name.
 *
 * Back is a text button, deliberately. It is not a peer of the action beside it:
 * Edit changes the record, Back just leaves. Giving it the same outlined box put
 * two equally-loud controls at opposite ends of one row competing for the same
 * attention — and matching their widths to fix the mismatch only made both of
 * them wider than the words inside them.
 */

export function DetailPageHeader({
  backLabel,
  onBack,
  title,
  badge,
  subtitle,
  actions,
}: DetailPageHeaderProps) {
  return (
    <Stack
      direction="row"
      spacing={2}
      useFlexGap
      // Wraps rather than crushes: a long company name beside a status chip and
      // two buttons will not fit a narrow viewport, and a title truncated to
      // make room for a button is the wrong trade.
      sx={{
        alignItems: { xs: 'stretch', sm: 'center' },
        flexWrap: 'wrap',
        minHeight: 40,
        rowGap: { xs: 1.5, sm: 2 },
      }}
    >
      {/* Back and the title share a baseline, so the two read as one line of
          text rather than as a control that happens to sit near a heading. The
          row around them stays centre-aligned — a status chip and an action
          button have no baseline worth matching. */}
      {/* At phone width Back takes a line of its own above the title: side by
          side, a long bulletin title was squeezed into a column a word wide
          with the actions painted across it. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'baseline' },
          gap: { xs: 0.5, sm: 2 },
          flex: 1,
          minWidth: 0,
        }}
      >
        <Button
          variant="text"
          startIcon={<Icons.ChevronLeft size={iconSize.md} />}
          onClick={onBack}
          sx={{
            flexShrink: 0,
            ml: { xs: -1, sm: 0 },
            // The chevron lines up with the menu icon in the topbar directly
            // above it. Both sit inside a container padded by the same step, so
            // matching them means matching the button padding too — and MUI
            // pulls a `startIcon` 4px left by default, which is exactly the
            // offset that made the two look a hair out of true.
            '& .MuiButton-startIcon': { marginLeft: 0 },
          }}
        >
          {backLabel}
        </Button>

        <Stack spacing={0.25} sx={{ minWidth: 0, maxWidth: '100%' }}>
          <Stack
            direction="row"
            spacing={1.5}
            useFlexGap
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Typography
              variant="h1"
              sx={{ fontSize: { xs: 20, sm: 22 }, lineHeight: 1.25, overflowWrap: 'anywhere' }}
            >
              {title}
            </Typography>
            {badge}
          </Stack>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Stack>
      </Box>

      {actions && (
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {actions}
        </Box>
      )}
    </Stack>
  );
}
